import {
  makeAddressArraySync,
  makeAddressSync,
  ProtocolConfig,
} from "@silo/types";
import { satisfies } from "effect/Function";
import { TOKEN_ADDRESS } from "../token-address";
import { MARKET_ADDRESS } from "../market-address";

export const optimism = satisfies<ProtocolConfig>()({
  firstBridgeSymbol: "ETH",
  secondBridgeSymbol: "USDC",
  marketWhitelist: new Set([
    MARKET_ADDRESS.optimism.OP,
    MARKET_ADDRESS.optimism.WBTC,
    MARKET_ADDRESS.optimism.wstETH,
    MARKET_ADDRESS.optimism.SNX,
    MARKET_ADDRESS.optimism.VELO,
    MARKET_ADDRESS.optimism.sfrxETH,
    MARKET_ADDRESS.optimism.ezETH,
  ]),
  pinnedMarkets: [],
  markets: {
    [MARKET_ADDRESS.optimism.ezETH]: {
      points: {
        base: {
          collateral: [
            { _tag: "eigenLayer" },
            { _tag: "renzo", label: "2x Renzo" },
          ],
        },
        firstBridge: {
          collateral: [{ _tag: "renzo", label: "1x Renzo" }],
        },
      },
    },
    [MARKET_ADDRESS.optimism.WLD]: {
      labelAsDeprecated: true,
    },
    [MARKET_ADDRESS.optimism.wUSDM]: {
      labelAsDeprecated: true,
    },
    [MARKET_ADDRESS.optimism.mooBIFI]: {
      depositMode: {
        base: "protectedOnly",
      },
    },
    [MARKET_ADDRESS.optimism.rETH]: {
      labelAsDeprecated: true,
    },
    [MARKET_ADDRESS.optimism.sUSD]: {
      labelAsDeprecated: true,
    },
    [MARKET_ADDRESS.optimism.uniBTC]: {
      labelAsDeprecated: true,
    },
    [MARKET_ADDRESS.optimism.tBTC]: {
      labelAsDeprecated: true,
    },
  },
  nativeRewards: {
    siloIncentivesControllerAddress: makeAddressSync(
      "0x6B2C80F5Be69E6d04De361b0dc640f8FC520DA7B"
    ),
    tokenAddress: TOKEN_ADDRESS.optimism.SILO,
    symbol: "SILO",
    decimals: 18,
    historicalShareTokens: {
      [TOKEN_ADDRESS.optimism.SILO]: makeAddressArraySync([
        // sezETH
        "0x934e7f714383C25D250C18e947dC8e51917f07CF",
        // sWETH-ezETH
        "0x9f3f998f52dda45D83234cF3A29995C391dB2b16",
        // sUSDC-ezETH
        "0x368E6755412184f87436D36558eC3B5f60230c93",
        // dWETH-ezETH
        "0x8eAA58232c1cd73C47a7Ad08b01a26f97D864d55",
        // sOP
        "0xb63701797c4cfAB868dD50bB9243A5085BcF0EE7",
        // sUSDC-OP
        "0x96000e67d7372DEEDEBbD4c9558Bd97935F7cEA5",
        // sWETH-OP
        "0x8282fD7b0d23e84d6c38Fbdae1D54add0245a3aF",
        // dWETH-OP
        "0x9Bd71A6685821A5801acAa76Cb2CfA8E66236c77",
        // srETH
        "0x86ff9efA0315454a1d914649E4622D22F9F84A58",
        // sUSDC-rETH
        "0xe7265df285ED7eC35cf502666Df997DF7B25034e",
        // sWETH-rETH
        "0xb69841c679fe733C550a351a896A2169Be5DA13E",
        // ssfrxETH
        "0x82e2df4b80Af42D4496Ed14CE9e2F4d8CA08Bbd3",
        // sWETH-fsrxETH
        "0x4383Da24d92c42D02DC577EC1144DF4dDF65A99d",
        // sSNX
        "0x98952F82726b4416aCEDd51B2204D90649732594", 
        // sUSDC-SNX
        "0x04a0ea87E56701D3C2F0fDA78a798d7603ca9851",
        // sWETH-SNX
        "0xA2FeC81f1F4Ef8e95b70B14C22755615270C108e",
        // ssUSD
        "0x545E39DA2DCa16873eBd5bdff4CAfa2ab387313F",
        // sUSDC-sUSD
        "0xD8360f0b27C06A1523222Ec74B8FF9f4DFbCb49B",
        // sWETH-sUSD
        "0x10199C38d99aC9f43410b375a6e2aFfCB5eFa1Bd",
        // stBTC
        "0x767C9ab86670A15bBe1af725bF052F4c1c1C4aE9",
        // sUSDC-tBTC
        "0x377433874E34460cCBe507644bAE604D10C22Cef",
        // sWETH-tBTC
        "0xd0B38ea4029f0CFD17F55FA27c0018BC09Db407f",
        // sVELO
        "0xEa29E611ADC9A6DB4b6881F428ec73Db40d1d418",
        // sUSDC-VELO
        "0x510F879686B076E960f0C04124C1753280436667",
        // sWETH-VELO
        "0xD1EE04eD6Dd6AB40aC88Dd7a7bDA0603AaBDFCe0",
        // sWBTC
        "0xB8191fB4b96708f54f4A0cc8d1A7913688957101",
        // sUSDC-WBTC
        "0xa305B001A5F8011c3F4A1f7847fD0C68c16b827b",
        // sWETH-WBTC
        "0x7ADF4cfcfd26f41695b2D9d853B6cFf1f59dc341",
        // dUSDC-WBTC
        "0xa97eA63f809ee690d48082c0ECb74ECFc592903F",
        // dWETH-WBTC
        "0xA03189B5a3329fbA1B4Cd61bD957eB5810C49c50",
        // swstETH
        "0x63535814e7046680A3A56B1C60F666557AECF1a3",
        // sUSDC-wstETH
        "0xf9641718F885660cC90E921CfE3D55122889b0C6",
        // sWETH-wstETH
        "0x633De586a3e4764f63B9ceF97F8c04d3b1d9895e",
        // spwUSDM
        "0xee0505B48cd7d9A911868E8E0B7d8Da47f2aC225",
        // sUSDC-wUSDM
        "0x58eA5075bea3ABd2fAC75F93b37925a67191DA17",
        // suniBTC
        "0xd693bf078f5e6dEB57e8e96FC96F36a045565614",
        // sWETH-uniBTC
        "0x10794989345124eDf994521B679F8706876B33DA",
        // sUSDC-uniBTC
        "0xED3Be80462f9412148eFa675A9C601A39CAFA6f2",
        // swUSDM
        "0xbaf984461DCf1c266a312ad79515a0D248D77678",
        //spMooBIFI
        "0x3C684F6A2492bd4b7D33dfF738325662be699aEB",
        //sUSDC-mooBIFI
        "0x01910928f404d3c1bbF2107B8E53A3aaA86De415",
        //sWETH-mooBIFI
        "0x508a6A256eE0fBb267EB941A2733a6fEfB39e3b9",
      ]),
    },
  },
} as const);
